import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, defer, filter, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faPersonWalking,
  faHands,
  faUser,
  faPerson,
  faEye,
  faDisplay,
  faShoePrints,
  faRotate,
  faHand,
  faHandsClapping,
  faCoffee,
  faLeaf,
  faSun,
  faWind,
  faVolumeHigh,
  faHeartPulse,
} from '@fortawesome/free-solid-svg-icons';
import { WorkTypeKey, normalizeWorkTypeKey } from '../shared/work-type.utils';

export interface Exercise {
  id?: string;
  title: string;
  description: string;
  videoUrl: string;
  icon: IconDefinition;
  type: 'active' | 'passive';
  category?: string;
  targetArea?: string;
  durationMinutes?: number;
  points?: number;
  posture?: ExercisePosture;
  /** Tipos de trabajo a los que aplica (vacio = todos). Solo viene del catalogo del servidor. */
  workTypes?: string[];
  categoryId?: string;
}

/** Postura que exige el ejercicio: avisa si hay que levantarse de la silla antes de empezar. */
export type ExercisePosture = 'sentado' | 'de-pie' | 'ambos';

const EXERCISE_POSTURE: Record<string, ExercisePosture> = {
  'Giro de Tronco Sentado': 'sentado',
  'Extensión Larga de Espalda': 'de-pie',
  'Palming y Descompresión Ocular': 'sentado',
  'Escaneo Corporal Consciente (Body Scan)': 'sentado',
  'Sonidos de la Naturaleza & Ondas Alfa': 'sentado',
  'Desconexión Digital & Caminata Consciente': 'de-pie',
  'Pausa de Café o Infusión Sin Pantallas': 'de-pie',
  'Luz Natural y Ventilación Profunda': 'de-pie',
  'Olas del Mar con Audífonos': 'sentado',
  'Respiración de Coherencia Cardíaca (5-5)': 'sentado',
  'Viaje Guiado a la Playa': 'sentado',
  'Relajación Profunda en Cuenta Regresiva': 'sentado',
  'Pausa Hipnótica Satisfactoria': 'sentado',
  'Baño de Sol Consciente': 'de-pie'
};

/** Posturas que llegan del catalogo del servidor (tienen prioridad sobre el mapa de respaldo). */
const remotePosture = new Map<string, ExercisePosture>();

/** Los demás ejercicios se pueden hacer sentado o de pie. */
export function getExercisePosture(title: string | undefined | null): ExercisePosture {
  const key = (title || '').trim();
  return remotePosture.get(key) ?? EXERCISE_POSTURE[key] ?? 'ambos';
}

/** Iconos disponibles por clave (la consola admin guarda la clave, p. ej. 'faEye'). */
const ICONS_BY_KEY: Record<string, IconDefinition> = {
  faPersonWalking, faHands, faUser, faPerson, faEye, faDisplay, faShoePrints, faRotate, faHand,
  faHandsClapping, faCoffee, faLeaf, faSun, faWind, faVolumeHigh, faHeartPulse
};

/** Ejercicio tal como lo entrega GET /exercises/catalog. */
export interface RemoteExercise {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  icon?: string | null;
  type?: string;
  category?: string | null;
  categoryId?: string | null;
  targetArea?: string | null;
  durationMinutes?: number;
  points?: number;
  posture?: string;
  workTypes?: string[];
}

export function mapRemoteExercise(raw: RemoteExercise): Exercise {
  const type: 'active' | 'passive' = raw.type === 'active' ? 'active' : 'passive';
  const posture: ExercisePosture = raw.posture === 'sentado' || raw.posture === 'de-pie' ? raw.posture : 'ambos';
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description || '',
    videoUrl: raw.videoUrl || '',
    icon: (raw.icon && ICONS_BY_KEY[raw.icon]) || (type === 'active' ? faPerson : faLeaf),
    type,
    category: raw.category || undefined,
    categoryId: raw.categoryId || undefined,
    targetArea: raw.targetArea || undefined,
    durationMinutes: raw.durationMinutes || 3,
    points: typeof raw.points === 'number' ? raw.points : 10,
    posture,
    workTypes: raw.workTypes || []
  };
}

@Injectable({
  providedIn: 'root',
})
export class ExerciseService {
  private readonly http = inject(HttpClient);

  /** Catalogo del servidor (null = aun no cargado o fallo la carga: se usa el estatico de respaldo). */
  private remote: Exercise[] | null = null;
  private readonly catalogVersion = new BehaviorSubject<number>(0);
  private loadStarted = false;
  /**
   * Emite cuando el catalogo disponible esta listo y cada vez que cambia (carga o limpieza).
   * No emite ANTES del primer intento de carga: asi las pantallas no muestran un instante el
   * catalogo estatico (con pausas que la empresa desactivo). La primera suscripcion dispara la
   * carga si nadie lo hizo todavia.
   */
  readonly catalogChanged$: Observable<number> = defer(() => {
    if (!this.loadStarted) {
      void this.loadRemoteCatalog();
    }
    return this.catalogVersion.pipe(filter(version => version > 0));
  });

  private administrativeExercisesFull: Exercise[] = [
    // ==========================================
    // PAUSAS ACTIVAS (12 EJERCICIOS FÍSICOS - MINIO RACKNERD)
    // ==========================================
    {
      title: 'Movilidad de Muñeca y Brazo',
      description: 'Flexiona y extiende la muñeca mientras elevas el brazo para activar antebrazos y prevenir el túnel carpiano.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_01_brazo_mu%C3%B1eca.mp4',
      icon: faHand,
      type: 'active',
      category: 'Brazos y Muñecas',
      targetArea: 'Muñecas y Antebrazos',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Antebrazo, Muñeca y Mano',
      description: 'Lleva la palma hacia abajo y tira de los dedos con la otra mano para liberar la sobrecarga del teclado y ratón.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_05_antebrazo_mu%C3%B1eca_mano.mp4',
      icon: faHands,
      type: 'active',
      category: 'Brazos y Muñecas',
      targetArea: 'Flexores y Tendones',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Libera Cuello y Hombros',
      description: 'Inclina la cabeza lateralmente y desciende el hombro contrario para aliviar la zona cervical y trapecios.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_03_cuello_hombro.mp4',
      icon: faPerson,
      type: 'active',
      category: 'Cuello y Hombros',
      targetArea: 'Zona Cervical y Trapecios',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Soltar Tensión de Cuello',
      description: 'Realiza microgiros controlados y flexiones suaves de cuello para desbloquear la base del cráneo y prevenir dolores de cabeza.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_13_cuello.mp4',
      icon: faUser,
      type: 'active',
      category: 'Cuello y Hombros',
      targetArea: 'Base del Cráneo y Cuello',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Giro de Tronco Sentado',
      description: 'Con la espalda erguida, rota el torso sujetando el respaldo de la silla para descomprimir las vértebras torácicas.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_07_espalda_columna_gira_tronco_sentado.mp4',
      icon: faRotate,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Columna Torácica y Lumbar',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Columna con Codos Atrás',
      description: 'Lleva los codos hacia atrás y abre el pecho para reactivar la musculatura interescapular y corregir la postura frente a la pantalla.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_08_espalda_columna_codos%20atr%C3%A1s.mp4',
      icon: faHandsClapping,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Espalda Media y Pectorales',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Activación de Brazos y Manos',
      description: 'Abre y cierra los dedos rítmicamente mientras elevas los brazos para reactivar el retorno venoso y linfático.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_09_brazos_manos.mp4',
      icon: faHands,
      type: 'active',
      category: 'Brazos y Muñecas',
      targetArea: 'Dedos, Muñecas y Brazos',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Apertura de Hombros y Cuello',
      description: 'Cruza un brazo frente al cuerpo y presiona sobre el codo para estirar deltoides y la cintura escapular.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_04_hombro_cuello_brazo.mp4',
      icon: faPerson,
      type: 'active',
      category: 'Cuello y Hombros',
      targetArea: 'Deltoides y Escápulas',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Espalda con Brazos Arriba',
      description: 'Entrelaza los dedos y estira los brazos sobre la cabeza alargando toda la columna vertebral y la caja torácica.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_02_espalda_tronco_brazos_arriba.mp4',
      icon: faPersonWalking,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Columna Completa',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Extensión Larga de Espalda',
      description: 'Lleva los brazos al frente y luego arquea suavemente hacia atrás activando la faja lumbar protectora.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_06_espalda_extiende_brazos.mp4',
      icon: faPerson,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Zona Lumbar y Espalda',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Brazos Atrás para Espalda Alta',
      description: 'Entrelaza las manos por detrás de la espalda y eleva suavemente los brazos para abrir hombros y pecho.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_10_espalda_brazos_atras.mp4',
      icon: faHandsClapping,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Pectorales y Escápulas',
      durationMinutes: 3,
      points: 15,
    },
    {
      title: 'Brazo Cruzado sobre Espalda',
      description: 'Cruza un brazo por detrás de la cabeza tomando el codo para soltar tríceps y dorsales laterales.',
      videoUrl: 'https://sb-storage.doctorelecktron.com/smartbreak-media/pausas_activas/ejercicio_12_brazo_espalda.mp4',
      icon: faPerson,
      type: 'active',
      category: 'Espalda y Tronco',
      targetArea: 'Tríceps y Dorsales',
      durationMinutes: 3,
      points: 15,
    },

    // ==========================================
    // PAUSAS PASIVAS (10 DESCANSO & RELAJACIÓN)
    // ==========================================
    {
      title: 'Regla 20-20-20 (Descanso Ocular)',
      description: 'Aparta la mirada de la pantalla cada 20 minutos y enfoca un objeto a 6 metros (20 pies) durante 20 segundos para relajar los músculos oculares y evitar la fatiga visual informática.',
      videoUrl: '',
      icon: faEye,
      type: 'passive',
      category: 'Salud Visual',
      targetArea: 'Músculos Oculares y Visión',
      durationMinutes: 2,
      points: 10,
    },
    {
      title: 'Palming y Descompresión Ocular',
      description: 'Frota tus palmas para generar calor suave y colócalas en forma de cuenco sobre tus ojos cerrados sin presionar. La oscuridad térmica alivia la tensión ocular acumulada.',
      videoUrl: '',
      icon: faHands,
      type: 'passive',
      category: 'Salud Visual',
      targetArea: 'Nervios Ópticos y Sienes',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Respiración 4-7-8 Anti-Estrés',
      description: 'Inhala por la nariz en 4 segundos, retén el aire con serenidad 7 segundos y exhala lentamente por la boca en 8 segundos. Reduce taquicardias y cortisol al instante.',
      videoUrl: '',
      icon: faHeartPulse,
      type: 'passive',
      category: 'Respiración y Calma',
      targetArea: 'Sistema Nervioso Autónomo',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Respiración Cuadrada (Box Breathing)',
      description: 'Inhala 4s, retén 4s, exhala 4s y sostén en vacío 4s. Técnica empleada para restaurar la concentración y el autodominio en momentos de alta carga laboral.',
      videoUrl: '',
      icon: faWind,
      type: 'passive',
      category: 'Respiración y Calma',
      targetArea: 'Foco Mental y Claridad',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Escaneo Corporal Consciente (Body Scan)',
      description: 'Cierra los ojos y rastrea tensiones inconscientes: separa los dientes, relaja el entrecejo, desciende los hombros y afloja los puños. Suelta toda la rigidez acumulada.',
      videoUrl: '',
      icon: faUser,
      type: 'passive',
      category: 'Mindfulness',
      targetArea: 'Mandíbula, Entrecejo y Postura',
      durationMinutes: 4,
      points: 10,
    },
    {
      title: 'Desconexión Digital & Caminata Consciente',
      description: 'Aléjate de toda pantalla durante 5 minutos. Camina con postura erguida por el pasillo o la oficina, sin revisar notificaciones ni celular.',
      videoUrl: '',
      icon: faPersonWalking,
      type: 'passive',
      category: 'Desconexión',
      targetArea: 'Circulación y Claridad Mental',
      durationMinutes: 5,
      points: 10,
    },
    {
      title: 'Hidratación Celular Consciente',
      description: 'Bebe un vaso entero de agua fresca a sorbos lentos. Una leve deshidratación del 1% disminuye la velocidad de procesamiento cognitivo y causa dolores de cabeza.',
      videoUrl: '',
      icon: faLeaf,
      type: 'passive',
      category: 'Bienestar',
      targetArea: 'Cerebro y Metabolismo',
      durationMinutes: 2,
      points: 10,
    },
    {
      title: 'Pausa de Café o Infusión Sin Pantallas',
      description: 'Prepárate un té, infusión o café y disfrútalo en un espacio diferente a tu escritorio, prestando atención exclusiva a sus aromas y calidez.',
      videoUrl: '',
      icon: faCoffee,
      type: 'passive',
      category: 'Bienestar',
      targetArea: 'Pausa Sensorial',
      durationMinutes: 5,
      points: 10,
    },
    {
      title: 'Sonidos de la Naturaleza & Ondas Alfa',
      description: 'Cierra los ojos y déjate envolver por sonidos de lluvia suave, bosque o mar para desconectar la mente de las tareas demandantes y resetear el foco.',
      videoUrl: '',
      icon: faVolumeHigh,
      type: 'passive',
      category: 'Sonoterapia',
      targetArea: 'Oído y Relajación Mental',
      durationMinutes: 4,
      points: 10,
    },
    {
      title: 'Luz Natural y Ventilación Profunda',
      description: 'Acércate a una ventana soleada o balcón, respira aire limpio y recibe luz diurna natural. Restablece los niveles de energía y reduce la fatiga mental de la tarde.',
      videoUrl: '',
      icon: faSun,
      type: 'passive',
      category: 'Energía',
      targetArea: 'Ritmo Circadiano y Pulmones',
      durationMinutes: 3,
      points: 10,
    },

    // Pausas pasivas inmersivas (nov 2026): escena, sonido y voz guía en el reproductor pasivo.
    {
      title: 'Olas del Mar con Audífonos',
      description: 'Ponte audífonos, cierra los ojos y sincroniza tu respiración con el vaivén de las olas. El sonido rítmico del mar baja el ritmo cardíaco y despeja la mente.',
      videoUrl: '',
      icon: faVolumeHigh,
      type: 'passive',
      category: 'Sonoterapia',
      targetArea: 'Oído y Ritmo Cardíaco',
      durationMinutes: 4,
      points: 10,
    },
    {
      title: 'Respiración de Coherencia Cardíaca (5-5)',
      description: 'Inhala 5 segundos y exhala 5 segundos, seis respiraciones por minuto. Sincroniza corazón y respiración para bajar la ansiedad y ganar claridad.',
      videoUrl: '',
      icon: faWind,
      type: 'passive',
      category: 'Respiración y Calma',
      targetArea: 'Corazón y Sistema Nervioso',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Viaje Guiado a la Playa',
      description: 'Visualización guiada con voz: cierra los ojos y camina por una playa tranquila mientras las olas se llevan tus preocupaciones. Un reseteo mental profundo en 5 minutos.',
      videoUrl: '',
      icon: faLeaf,
      type: 'passive',
      category: 'Relajación Guiada',
      targetArea: 'Mente y Emociones',
      durationMinutes: 5,
      points: 10,
    },
    {
      title: 'Relajación Profunda en Cuenta Regresiva',
      description: 'Una voz te guía a soltar el cuerpo de pies a cabeza mientras cuentas del 10 al 1, con ondas alfa de fondo. Técnica de relajación inducida, similar a una autohipnosis ligera.',
      videoUrl: '',
      icon: faHeartPulse,
      type: 'passive',
      category: 'Relajación Guiada',
      targetArea: 'Relajación Total',
      durationMinutes: 6,
      points: 10,
    },
    {
      title: 'Frases que Recargan tu Energía',
      description: 'Frases motivacionales que aparecen y se leen en voz alta con música suave. Cambia el diálogo interno y vuelve a tu tarea con otra actitud.',
      videoUrl: '',
      icon: faSun,
      type: 'passive',
      category: 'Motivación',
      targetArea: 'Ánimo y Autoconfianza',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Pausa Hipnótica Satisfactoria',
      description: 'Formas de colores que fluyen como una lámpara de lava y ondas que creas al tocar la pantalla, cada una con su nota. Estímulo visual y sonoro que genera calma y satisfacción.',
      videoUrl: '',
      icon: faEye,
      type: 'passive',
      category: 'Sensorial',
      targetArea: 'Vista, Oído y Calma',
      durationMinutes: 3,
      points: 10,
    },
    {
      title: 'Baño de Sol Consciente',
      description: 'Sal al sol 10 minutos con la cara y los brazos descubiertos, sin mirarlo directamente. La luz natural regula tu reloj interno, sube el ánimo y aporta vitamina D.',
      videoUrl: '',
      icon: faSun,
      type: 'passive',
      category: 'Energía',
      targetArea: 'Ritmo Circadiano y Ánimo',
      durationMinutes: 10,
      points: 10,
    },
  ];

  // Catálogos específicos y ordenados por enfoque ergonómico para cada exigencia física:
  // 1. Sedentario / Oficina: Enfoque en cuello, espalda, muñecas y vista.
  private sedentaryExercises: Exercise[] = [
    // Cuello, vista, muñecas y espalda sentada prioritarios
    this.administrativeExercisesFull[2], // Libera Cuello y Hombros
    this.administrativeExercisesFull[3], // Soltar Tensión de Cuello
    this.administrativeExercisesFull[0], // Movilidad de Muñeca y Brazo
    this.administrativeExercisesFull[1], // Antebrazo, Muñeca y Mano
    this.administrativeExercisesFull[4], // Giro de Tronco Sentado
    this.administrativeExercisesFull[5], // Columna con Codos Atrás
    this.administrativeExercisesFull[7], // Apertura de Hombros y Cuello
    this.administrativeExercisesFull[8], // Espalda con Brazos Arriba
    this.administrativeExercisesFull[6], // Activación de Brazos y Manos
    this.administrativeExercisesFull[9], // Extensión Larga de Espalda
    this.administrativeExercisesFull[10], // Brazos Atrás para Espalda Alta
    this.administrativeExercisesFull[11], // Brazo Cruzado sobre Espalda
    // Pasivos con fuerte énfasis en salud visual y descompresión cervical
    this.administrativeExercisesFull[12], // Regla 20-20-20
    this.administrativeExercisesFull[13], // Palming Ocular
    this.administrativeExercisesFull[14], // Respiración 4-7-8
    this.administrativeExercisesFull[15], // Box Breathing
    this.administrativeExercisesFull[16], // Body Scan
    this.administrativeExercisesFull[17], // Caminata Consciente
    this.administrativeExercisesFull[18], // Hidratación Celular
    this.administrativeExercisesFull[19], // Pausa Sensorial
    this.administrativeExercisesFull[20], // Sonidos de la Naturaleza
    this.administrativeExercisesFull[21], // Luz Natural
    this.administrativeExercisesFull[22], // Olas del Mar con Audífonos
    this.administrativeExercisesFull[23], // Respiración de Coherencia Cardíaca (5-5)
    this.administrativeExercisesFull[24], // Viaje Guiado a la Playa
    this.administrativeExercisesFull[25], // Relajación Profunda en Cuenta Regresiva
    this.administrativeExercisesFull[26], // Frases que Recargan tu Energía
    this.administrativeExercisesFull[27], // Pausa Hipnótica Satisfactoria
    this.administrativeExercisesFull[28], // Baño de Sol Consciente
  ];

  // 2. Móvil / De pie: Enfoque en circulación de piernas, descanso lumbar y descarga de pantorrillas.
  private mobileExercises: Exercise[] = [
    this.administrativeExercisesFull[9],  // Extensión Larga de Espalda (alivio lumbar)
    this.administrativeExercisesFull[8],  // Espalda con Brazos Arriba (elongación axial)
    this.administrativeExercisesFull[4],  // Giro de Tronco Sentado (descanso articular sentado)
    this.administrativeExercisesFull[6],  // Activación de Brazos y Manos (retorno circulatorio)
    this.administrativeExercisesFull[10], // Brazos Atrás para Espalda Alta
    this.administrativeExercisesFull[2],  // Libera Cuello y Hombros
    this.administrativeExercisesFull[5],  // Columna con Codos Atrás
    this.administrativeExercisesFull[7],  // Apertura de Hombros y Cuello
    this.administrativeExercisesFull[0],  // Movilidad de Muñeca y Brazo
    this.administrativeExercisesFull[1],  // Antebrazo, Muñeca y Mano
    this.administrativeExercisesFull[3],  // Soltar Tensión de Cuello
    this.administrativeExercisesFull[11], // Brazo Cruzado sobre Espalda
    // Pasivos con énfasis en descanso sentado y retorno venoso
    this.administrativeExercisesFull[17], // Caminata / Pausa Consciente
    this.administrativeExercisesFull[18], // Hidratación Celular
    this.administrativeExercisesFull[21], // Luz Natural y Ventilación
    this.administrativeExercisesFull[16], // Body Scan
    this.administrativeExercisesFull[14], // Respiración 4-7-8
    this.administrativeExercisesFull[15], // Box Breathing
    this.administrativeExercisesFull[19], // Pausa de Café / Sentado
    this.administrativeExercisesFull[12], // Regla 20-20-20
    this.administrativeExercisesFull[13], // Palming
    this.administrativeExercisesFull[20], // Sonidos de la Naturaleza
    this.administrativeExercisesFull[22], // Olas del Mar con Audífonos
    this.administrativeExercisesFull[23], // Respiración de Coherencia Cardíaca (5-5)
    this.administrativeExercisesFull[24], // Viaje Guiado a la Playa
    this.administrativeExercisesFull[25], // Relajación Profunda en Cuenta Regresiva
    this.administrativeExercisesFull[26], // Frases que Recargan tu Energía
    this.administrativeExercisesFull[27], // Pausa Hipnótica Satisfactoria
    this.administrativeExercisesFull[28], // Baño de Sol Consciente
  ];

  // 3. Manual / Operativo pesado: Enfoque en descompresión de columna, brazos y articulaciones.
  private manualExercises: Exercise[] = [
    this.administrativeExercisesFull[11], // Brazo Cruzado sobre Espalda (alivio dorsal y tríceps)
    this.administrativeExercisesFull[10], // Brazos Atrás para Espalda Alta (apertura torácica)
    this.administrativeExercisesFull[9],  // Extensión Larga de Espalda (faja lumbar)
    this.administrativeExercisesFull[0],  // Movilidad de Muñeca y Brazo (túnel carpiano y flexores)
    this.administrativeExercisesFull[1],  // Antebrazo, Muñeca y Mano
    this.administrativeExercisesFull[7],  // Apertura de Hombros y Cuello
    this.administrativeExercisesFull[5],  // Columna con Codos Atrás
    this.administrativeExercisesFull[2],  // Libera Cuello y Hombros
    this.administrativeExercisesFull[8],  // Espalda con Brazos Arriba
    this.administrativeExercisesFull[4],  // Giro de Tronco Sentado
    this.administrativeExercisesFull[3],  // Soltar Tensión de Cuello
    this.administrativeExercisesFull[6],  // Activación de Brazos y Manos
    // Pasivos de recuperación articular y alivio muscular
    this.administrativeExercisesFull[16], // Body Scan (soltar contracturas)
    this.administrativeExercisesFull[14], // Respiración 4-7-8 (anti-fatiga física)
    this.administrativeExercisesFull[18], // Hidratación Celular
    this.administrativeExercisesFull[20], // Sonidos de Naturaleza
    this.administrativeExercisesFull[21], // Ventilación Profunda
    this.administrativeExercisesFull[15], // Box Breathing
    this.administrativeExercisesFull[17], // Caminata Suave
    this.administrativeExercisesFull[19], // Pausa Reparadora
    this.administrativeExercisesFull[12], // Descanso Visual
    this.administrativeExercisesFull[13], // Palming
    this.administrativeExercisesFull[22], // Olas del Mar con Audífonos
    this.administrativeExercisesFull[23], // Respiración de Coherencia Cardíaca (5-5)
    this.administrativeExercisesFull[24], // Viaje Guiado a la Playa
    this.administrativeExercisesFull[25], // Relajación Profunda en Cuenta Regresiva
    this.administrativeExercisesFull[26], // Frases que Recargan tu Energía
    this.administrativeExercisesFull[27], // Pausa Hipnótica Satisfactoria
    this.administrativeExercisesFull[28], // Baño de Sol Consciente
  ];

  // 4. Dinámico / Mixto: Enfoque en alivio muscular integral y balance postural.
  private dynamicExercises: Exercise[] = [
    this.administrativeExercisesFull[8],  // Espalda con Brazos Arriba
    this.administrativeExercisesFull[2],  // Libera Cuello y Hombros
    this.administrativeExercisesFull[4],  // Giro de Tronco Sentado
    this.administrativeExercisesFull[6],  // Activación de Brazos y Manos
    this.administrativeExercisesFull[9],  // Extensión Larga de Espalda
    this.administrativeExercisesFull[0],  // Movilidad de Muñeca y Brazo
    this.administrativeExercisesFull[10], // Brazos Atrás para Espalda Alta
    this.administrativeExercisesFull[5],  // Columna con Codos Atrás
    this.administrativeExercisesFull[7],  // Apertura de Hombros y Cuello
    this.administrativeExercisesFull[1],  // Antebrazo, Muñeca y Mano
    this.administrativeExercisesFull[11], // Brazo Cruzado sobre Espalda
    this.administrativeExercisesFull[3],  // Soltar Tensión de Cuello
    // Pasivos balanceados
    this.administrativeExercisesFull[12], // Regla 20-20-20
    this.administrativeExercisesFull[15], // Box Breathing
    this.administrativeExercisesFull[17], // Desconexión y Caminata
    this.administrativeExercisesFull[19], // Pausa sin pantallas
    this.administrativeExercisesFull[14], // Respiración 4-7-8
    this.administrativeExercisesFull[18], // Hidratación Celular
    this.administrativeExercisesFull[13], // Palming
    this.administrativeExercisesFull[16], // Body Scan
    this.administrativeExercisesFull[21], // Luz Natural
    this.administrativeExercisesFull[20], // Sonidos de la Naturaleza
    this.administrativeExercisesFull[22], // Olas del Mar con Audífonos
    this.administrativeExercisesFull[23], // Respiración de Coherencia Cardíaca (5-5)
    this.administrativeExercisesFull[24], // Viaje Guiado a la Playa
    this.administrativeExercisesFull[25], // Relajación Profunda en Cuenta Regresiva
    this.administrativeExercisesFull[26], // Frases que Recargan tu Energía
    this.administrativeExercisesFull[27], // Pausa Hipnótica Satisfactoria
    this.administrativeExercisesFull[28], // Baño de Sol Consciente
  ];

  /**
   * Carga el catalogo EFECTIVO del usuario (publicado, activo para su empresa y aplicable a su tipo
   * de trabajo). Si falla, se conserva el catalogo estatico para que la app siga funcionando.
   */
  async loadRemoteCatalog(): Promise<void> {
    this.loadStarted = true;
    try {
      const res = await firstValueFrom(
        this.http.get<{ exercises: RemoteExercise[] }>(`${environment.apiUrl}/exercises/catalog`)
      );
      this.remote = (res.exercises ?? []).map(mapRemoteExercise);
      remotePosture.clear();
      for (const e of this.remote) {
        if (e.posture) remotePosture.set(e.title, e.posture);
      }
    } catch {
      this.remote = null;
    }
    this.catalogVersion.next(this.catalogVersion.value + 1);
  }

  /** Al cerrar sesion: el siguiente usuario debe recibir SU catalogo, no el del anterior. */
  clearRemoteCatalog(): void {
    this.remote = null;
    this.loadStarted = false;
    remotePosture.clear();
    this.catalogVersion.next(this.catalogVersion.value + 1);
  }


  /** Busca un ejercicio por su titulo exacto en TODO el catalogo (enlaces profundos y sugerencias). */
  findExerciseByTitle(title: string | null | undefined): Exercise | undefined {
    const wanted = (title || '').trim();
    if (!wanted) return undefined;
    return (this.remote ?? this.administrativeExercisesFull).find(e => e.title === wanted);
  }

  getExercises(workspaceType: WorkTypeKey | string): Exercise[] {
    // El servidor ya filtro por empresa y tipo de trabajo; una lista vacia es valida (todo desactivado).
    if (this.remote) return this.remote;
    const key = normalizeWorkTypeKey(workspaceType);
    switch (key) {
      case 'sedentario':
        return this.sedentaryExercises;
      case 'movil':
        return this.mobileExercises;
      case 'manual':
        return this.manualExercises;
      case 'dinamico':
      default:
        return this.dynamicExercises;
    }
  }

  getActiveExercises(workspaceType: WorkTypeKey | string): Exercise[] {
    const allExercises = this.getExercises(workspaceType);
    return allExercises.filter(exercise => exercise.type === 'active');
  }

  getPassiveExercises(workspaceType: WorkTypeKey | string): Exercise[] {
    const allExercises = this.getExercises(workspaceType);
    return allExercises.filter(exercise => exercise.type === 'passive');
  }
}
